import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const ProductSlideshow = ({ slides = [], autoplayMs = 6000 }) => {
  const [idx, setIdx] = useState(0);
  const total = slides.length;
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  const go = (next) => {
    setIdx((i) => (next + total) % total);
  };

  // autoplay
  useEffect(() => {
    if (!autoplayMs || total <= 1) return;
    timerRef.current = setTimeout(() => go(idx + 1), autoplayMs);
    return () => clearTimeout(timerRef.current);
  }, [idx, autoplayMs, total]);

  // keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.matches(":hover, :focus-within")) return;
      if (e.key === "ArrowRight") go(idx + 1);
      if (e.key === "ArrowLeft") go(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);

  if (!total) return null;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full min-h-[480px] lg:min-h-0 bg-white border border-[var(--yono-border)] overflow-hidden group"
      data-testid="product-slideshow"
      tabIndex={0}
    >
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-700 ease-out"
          style={{
            opacity: i === idx ? 1 : 0,
            pointerEvents: i === idx ? "auto" : "none",
          }}
          aria-hidden={i !== idx}
        >
          <img
            src={s.src}
            alt={s.alt}
            className={`w-full h-full ${
              s.fit === "contain"
                ? "object-contain bg-[var(--yono-beige)]"
                : "object-cover"
            }`}
          />
        </div>
      ))}

      {/* Prev / Next */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(idx - 1)}
            aria-label="Image précédente"
            data-testid="slideshow-prev"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 hover:bg-white border border-[var(--yono-border)] backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => go(idx + 1)}
            aria-label="Image suivante"
            data-testid="slideshow-next"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 hover:bg-white border border-[var(--yono-border)] backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
        </>
      )}

      {/* Dots + counter */}
      {total > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-2 bg-white/85 backdrop-blur-sm border border-[var(--yono-border)]">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Aller à l'image ${i + 1}`}
              data-testid={`slideshow-dot-${i}`}
              className={`h-[3px] transition-all ${
                i === idx
                  ? "w-8 bg-[var(--yono-soft-black)]"
                  : "w-4 bg-[var(--yono-border)] hover:bg-[var(--yono-warm-grey)]"
              }`}
            />
          ))}
          <span className="ml-2 font-mono text-[10px] tracking-[0.18em] text-[var(--yono-warm-grey)]">
            {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProductSlideshow;
